import { AlertTriangle, Copy, RefreshCw, Send } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useIncidentReport } from "../../hooks/useIncidentReport";
import { PageMeta } from "../PageMeta";

export function ErrorPage() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const { report, status: reportStatus } = useIncidentReport();

  const handleSignOut = () => {
    localStorage.removeItem('adjudon_auth_token');
    localStorage.removeItem('adjudon_user');
    navigate('/login');
  };

  const userEmail = (() => {
    try {
      const userStr = localStorage.getItem('adjudon_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.email || null;
      }
    } catch (e) {
      return null;
    }
    return null;
  })();

  const timestamp = new Date().toISOString();

  const handleCopy = () => {
    const lines = [
      `ADJUDON ERROR REPORT`,
      `STATUS: ERROR: UNKNOWN`,
      userEmail ? `USER: ${userEmail}` : null,
      `TIME: ${timestamp}`,
    ].filter(Boolean).join("\n");
    navigator.clipboard.writeText(lines as string).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleContactSupport = () => {
    report({ type: "error_page", message: "Runtime crash (ErrorBoundary)", timestamp });
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[var(--background)]">
      <PageMeta
        title="Error – ADJUDON"
        description="An unexpected error occurred."
        noindex={true}
      />

      <div className="adp-card" style={{ maxWidth: "440px", width: "100%", padding: "40px" }}>
        {/* Icon */}
        <div
          className="w-10 h-10 flex items-center justify-center border mb-8"
          style={{ borderColor: "var(--border)", background: "var(--surface-1)" }}
        >
          <AlertTriangle className="w-4 h-4" style={{ color: "var(--muted-foreground)" }} />
        </div>

        {/* Label + Title + Subtitle */}
        <div className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground mb-1">
          System Error
        </div>
        <h1 className="text-xl font-semibold text-foreground mb-3">
          Something went wrong
        </h1>
        <p className="text-sm text-muted-foreground" style={{ lineHeight: "1.6", maxWidth: "360px" }}>
          An unexpected runtime error has occurred. This error has been logged.
        </p>

        {/* Divider */}
        <div className="border-t border-border my-8" />

        {/* Diagnostics */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground">
            Diagnostics
          </span>
          <button
            onClick={handleCopy}
            className="adp-btn flex items-center gap-1 px-2 py-1 text-[10px] text-mono uppercase tracking-wider"
          >
            <Copy className="w-3 h-3" />
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-6">
            <span className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground w-14 flex-shrink-0 pt-px">Status</span>
            <span className="text-xs text-mono text-foreground">ERROR: UNKNOWN</span>
          </div>
          {userEmail && (
            <div className="flex items-start gap-6">
              <span className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground w-14 flex-shrink-0 pt-px">User</span>
              <span className="text-xs text-mono text-foreground">{userEmail}</span>
            </div>
          )}
          <div className="flex items-start gap-6">
            <span className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground w-14 flex-shrink-0 pt-px">Time</span>
            <span className="text-xs text-mono text-foreground">{timestamp}</span>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-border my-8" />

        {/* Quick Navigation */}
        <div className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground mb-3">
          Quick Navigation
        </div>
        <div className="flex flex-wrap gap-2 mb-8">
          {[
            { to: "/dashboard", label: "Overview" },
            { to: "/dashboard/systems", label: "AI Systems" },
            { to: "/dashboard/decision-log", label: "Decision Log" },
            { to: "/dashboard/policy-engine", label: "Policies" },
          ].map(({ to, label }) => (
            <Link key={to} to={to} className="adp-btn px-4 py-2 text-[11px] text-mono uppercase tracking-wider">
              {label}
            </Link>
          ))}
        </div>

        {/* CTAs */}
        <div className="flex flex-col gap-2">
          <button
            onClick={() => window.location.reload()}
            className="adp-btn w-full py-2.5 text-[11px] text-mono uppercase tracking-wider flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Try again
          </button>
          <Link
            to="/dashboard"
            className="adp-btn w-full py-2.5 text-[11px] text-mono uppercase tracking-wider flex items-center justify-center"
          >
            Back to dashboard
          </Link>
          <button
            onClick={handleContactSupport}
            disabled={reportStatus === "sending"}
            className="adp-btn w-full py-2.5 text-[11px] text-mono uppercase tracking-wider flex items-center justify-center gap-2 text-muted-foreground"
          >
            <Send className="w-3.5 h-3.5" />
            {reportStatus === "sending" ? "Sending..." : reportStatus === "sent" ? "Report Sent ✓" : "Contact Support"}
          </button>
          {userEmail && (
            <button
              onClick={handleSignOut}
              className="adp-btn w-full py-2.5 text-[11px] text-mono uppercase tracking-wider text-muted-foreground"
            >
              Sign in as a different user
            </button>
          )}
        </div>

        {/* Support Footer */}
        <div className="border-t border-border mt-8 pt-4 flex gap-4">
          <a
            href="https://status.adjudon.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
          >
            Status.Adjudon.com
          </a>
          <a
            href="mailto:support@adjudon.com"
            className="text-[10px] text-mono uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
          >
            Support@Adjudon.com
          </a>
        </div>
      </div>
    </div>
  );
}
